import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderNoteDetailModel } from 'src/app/models/shopping/provider-note-detail.model';
import { ProviderNoteModel } from 'src/app/models/shopping/provider-note.model';
import { ProviderNoteArticleSelectTableModel } from 'src/app/models/tables/shopping/provider-note-article-select-table.model';
import { ProviderNoteDetailService } from 'src/app/services/shopping/provider-note-detail.service';
import { ProviderNoteService } from 'src/app/services/shopping/provider-note.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-provider-note-detail',
  templateUrl: './register-provider-note-detail.component.html',
  styleUrls: ['./register-provider-note-detail.component.scss'],
  standalone: false
})
export class RegisterProviderNoteDetailComponent implements OnInit, OnChanges {

  @ViewChild('selectArticle') articleModalComponent?: CustomModalComponent | null = null;

  private readonly formBuilder = inject(FormBuilder);
  private readonly providerNoteService = inject(ProviderNoteService);
  private readonly providerNoteDetailService = inject(ProviderNoteDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() permissionUpdatable: boolean = false;
  @Input() providerNoteDetailModel: ProviderNoteDetailModel = new ProviderNoteDetailModel();
  @Input() providerNoteModel: ProviderNoteModel = new ProviderNoteModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() providerNoteContract = new EventEmitter<ProviderNoteModel>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  ascendantArticle: boolean = true;
  codeNameArticle: string = '';
  columnSortArticle: string | null = null;
  conceptInvoicingId: number = 0;
  dataArticle: PagedResponseModel<ProviderNoteArticleSelectTableModel> = new PagedResponseModel<ProviderNoteArticleSelectTableModel>();
  disableSave: boolean = false;
  filtersArticle: ColumnResponseModel[] = [];
  formNoteDetail: FormGroup = new FormGroup({});
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelect$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: [],
  });
  limitArticle: number = 10;
  output$ = new Subject<any>();
  pageArticle: number = 1;
  providerNoteDetailId: number = 0;
  subscription: Subscription;
  component = CustomTableSelectComponent;

  async ngOnInit() {
    this.validateForm(new ProviderNoteDetailModel());
    await this.getColumnsArticle();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const providerNoteDetailModel = changes['providerNoteDetailModel'];
    if (providerNoteDetailModel && !providerNoteDetailModel.firstChange) {
      this.providerNoteDetailId = this.providerNoteDetailModel.providerNoteDetailId;
      this.validateForm(this.providerNoteDetailModel);
    }
  }

  changeQuantity(quantity: number) {
    if (quantity > this.providerNoteDetailModel.quantityInvoiced) {
      this.toastHandlerService.showToast(
        'warning',
        'La cantidad no puede ser mayor a la cantidad facturada.'
      );
      this.formNoteDetail.get('quantity')?.setValue(0);
    }
  }

  changeTotalValue(totalValue: number) {
    if (totalValue > this.providerNoteDetailModel.unitValueInvoiced * this.providerNoteDetailModel.quantityInvoiced) {
      this.toastHandlerService.showToast(
        'warning',
        'El valor total no puede ser mayor al valor total facturado.'
      );
      this.formNoteDetail.get('totalValue')?.setValue(0);
      this.formNoteDetail.get('ivaBase')?.setValue(0);
      return;
    }
    this.formNoteDetail.get('ivaBase')?.setValue(totalValue);
  }

  clearForm() {
    this.codeNameArticle = '';
    this.providerNoteDetailModel = new ProviderNoteDetailModel();
    this.validateForm(this.providerNoteDetailModel);
    this.isFormView.emit(false);
  }

  async getAllArticlesForSelect() {
    try {
      this.dataArticle = await firstValueFrom(
        this.providerNoteDetailService.getArticlesForProviderNote(
          this.providerNoteModel.providerNoteId, this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle
        )
      );
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de articulos.', error);
    }
  }

  async getColumnsArticle() {
    try {
      if (this.headerArticle.length === 0) {
        this.headerArticle = await firstValueFrom(this.providerNoteDetailService.getColumnsForArticleCaptureProviderNote());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getProviderNoteById(providerNoteId: number) {
    try {
      this.providerNoteModel = await firstValueFrom(this.providerNoteService.getProviderNoteById(providerNoteId));
      this.providerNoteContract.emit(this.providerNoteModel);
    } catch (error) {
      console.error('Error al buscar la nota proveedor.', error);
    }
  }

  async handleSelectedItems(selectedItems: any) {
    if (!selectedItems || selectedItems.length === 0) {
      console.warn('No se seleccionaron artículos.');
      return;
    }
    if (selectedItems.length === 1) {
      const item = selectedItems[0];
      this.providerNoteDetailId = 0;
      this.conceptInvoicingId = item.conceptInvoicingId;
      this.providerNoteDetailModel = Object.assign(new ProviderNoteDetailModel(), item, {
        providerInvoiceDetailId: item.id,
        quantity: item.quantityInvoiced,
        ivaBase: item.totalValue,
      });
      this.validateForm(this.providerNoteDetailModel);
    }

    if (selectedItems.length > 1) {
      this.providerNoteDetailId = 0;
      this.codeNameArticle = '';
      this.providerNoteDetailModel = new ProviderNoteDetailModel();
      this.validateForm(this.providerNoteDetailModel);
      const request: ProviderNoteDetailModel[] = selectedItems.map((row: any) => Object.assign(new ProviderNoteDetailModel(), {
        providerNoteId: this.providerNoteModel.providerNoteId,
        providerInvoiceDetailId: row.id,
        conceptInvoicingId: row.conceptInvoicingId,
        quantity: row.quantityInvoiced,
        ivaRate: row.ivaRate,
        ivaBase: row.totalValue,
        totalValue: row.totalValue,
      }));
      try {
        await firstValueFrom(this.providerNoteDetailService.createBulkProviderNoteDetail(request));
        this.toastHandlerService.showToast('success', `Se agregaron ${selectedItems.length} detalles a la nota proveedor.`);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al crear los detalles de la nota proveedor.', error);
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    this.getAllArticlesForSelect();
  }

  async openArticleModal() {
    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsArticle(),
      this.getAllArticlesForSelect()
    ]);
    this.updateInputs();
    this.articleModalComponent && this.articleModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    Object.keys(this.formNoteDetail.controls).forEach((key) => {
      const control = this.formNoteDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formNoteDetail.invalid) {
      this.showValidationMessages();
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear o modificar notas proveedores. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    const formValue = this.formNoteDetail.getRawValue() as ProviderNoteDetailModel;
    formValue.providerNoteId = this.providerNoteModel.providerNoteId;

    if (this.providerNoteDetailId === 0) {
      const newProviderNoteDetail = Object.assign(new ProviderNoteDetailModel(), formValue);
      try {
        await firstValueFrom(this.providerNoteDetailService.createProviderNoteDetail(newProviderNoteDetail));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la nota proveedor fue creado exitosamente.'
        );
        this.updateListDetail.emit(true);
        this.clearForm();
      } catch (error) {
        console.error('Error al crear  el detalle de la nota proveedor.', error);
      }
    } else {
      Object.assign(this.providerNoteDetailModel, formValue);
      try {
        await firstValueFrom(this.providerNoteDetailService.updateProviderNoteDetail(this.providerNoteDetailModel));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la nota proveedor fue modificado exitosamente.'
        );
        this.updateListDetail.emit(true);
        this.clearForm();
      } catch (error) {
        console.error('Error al actualizar el detalle de la nota proveedor.', error);
      }
    }
    this.getProviderNoteById(this.providerNoteModel.providerNoteId);
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      providerInvoiceDetailId: {
        required: 'El campo Factura Proveedor Detalle es obligatorio.',
        min: 'Debe seleccionar una Factura Proveedor Detalle válida.'
      },
      conceptInvoicingId: {
        required: 'El campo Concepto Facturación es obligatorio.',
        min: 'Debe seleccionar un Concepto Facturación válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'Debe ingresar una Cantidad igual o superior a 0.'
      },
      unitValue: {
        required: 'El campo Valor Unitario es obligatorio.',
        min: 'Debe ingresar un Valor Unitario igual o superior a 0.'
      },
      ivaRate: {
        required: 'El campo Porcentaje IVA es obligatorio.',
        min: 'Debe ingresar un Porcentaje IVA igual o superior a 0.'
      },
      ivaBase: {
        required: 'El campo Base IVA es obligatorio.',
        min: 'Debe ingresar un Base IVA igual o superior a 0.'
      },
      totalValue: {
        required: 'El campo Valor Total es obligatorio.',
        min: 'Debe ingresar un Valor Total igual o superior a 0.'
      },
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formNoteDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  updateInputs() {
    this.inputsForArticlesSelect$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle
    });
  }

  validateForm(providerNoteDetail: ProviderNoteDetailModel) {
    this.formNoteDetail = this.formBuilder.group({
      providerNoteId: [providerNoteDetail.providerNoteId],
      providerInvoiceDetailId: [providerNoteDetail.providerInvoiceDetailId, [Validators.required, Validators.min(1)]],
      conceptInvoicingId: [providerNoteDetail.conceptInvoicingId, [Validators.required, Validators.min(1)]],
      quantity: [providerNoteDetail.quantity, [Validators.required, Validators.min(0)]],
      unitValueInvoiced: [{ value: providerNoteDetail.unitValueInvoiced, disabled: true }, [Validators.min(0)]],
      quantityInvoiced: [{ value: providerNoteDetail.quantityInvoiced, disabled: true }, [Validators.min(0)]],
      totalValueInvoiced: [{ value: providerNoteDetail.quantityInvoiced * providerNoteDetail.unitValueInvoiced, disabled: true }, [Validators.min(0)]],
      ivaRate: [{ value: providerNoteDetail.ivaRate, disabled: true }, [Validators.min(0), Validators.max(100)]],
      ivaBase: [{ value: providerNoteDetail.ivaBase, disabled: true }, [Validators.min(0)]],
      totalValue: [providerNoteDetail.totalValue, [Validators.min(1)]],
    });
    this.codeNameArticle = [providerNoteDetail.articleCode, providerNoteDetail.articleName].filter(Boolean).join(' - ');
  }

}
